import React, { useState, useEffect } from "react";
import axios from "axios";
import { Edit, Trash2, X, Plus } from "lucide-react";
import { baseUrl } from "../../common/constants";
import _axios from "../../utils/api/_axios";
import ButtonLoader from "../../components/ReusableElements/Button/ButtonLoader";
import { toast } from "react-toastify";

const ServicesPage = ({ userId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [formData, setFormData] = useState({
    serviceName: "",
    description: "",
  });
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const userName = localStorage.getItem("userName");
  
  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await _axios.get(`${baseUrl}/servicePerformed/get-all`);
      setServices(response.data.payload);
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.serviceName.trim()) {
      return alert("Service name is required");
    }
    try {
      setLoading(true);
      let response;
      if (editMode) {
        response = await _axios.put(`${baseUrl}/servicePerformed/${selectedServiceId}`, formData);
      } else {
        response = await _axios.post(`${baseUrl}/servicePerformed`, { ...formData, createdBy: userName });
      }
      if(response.status === 200 || response.status === 201){
        toast.success("operation successful");
        fetchServices();
        closeModal();
      }
    } catch (error) {
      console.error("Error saving service:", error);
      alert("Failed to save service");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (service) => {
    setFormData({
      serviceName: service.serviceName,
      description: service.description,
      createdBy: service.createdBy,
    });
    setSelectedServiceId(service._id);
    setEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;
    let response;
    try {
      response = await _axios.put(`${baseUrl}/servicePerformed/${id}`,{isDeleted: true});
      if(response?.status === 200 || response?.status === 201){
        toast.success("service deleted successfully")
      }
      fetchServices();
    } catch (error) {
      toast.error("Failed to delete service");
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditMode(false);
    setSelectedServiceId(null);
    setFormData({ serviceName: "", description: "", createdBy: "" });
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6 flex justify-center">
      <div className="max-w-7xl w-full bg-white p-6 rounded-lg shadow-md">
        <h1 className="text-2xl font-semibold text-center text-gray-800 mb-6">
          Services Management
        </h1>

        <div className="flex justify-between mb-6">
          <button
            className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg shadow-md hover:bg-gray-500"
            onClick={() => setIsModalOpen(true)}
          >
            <Plus size={18} /> Add New Service
          </button>
        </div>

        <div className="overflow-x-auto bg-white shadow-md rounded-lg p-6">
          <table className="w-full border-collapse">
            <thead>
              <tr className="text-gray-600 text-sm border-b">
                <th className="p-4 text-left font-medium">Service Name</th>
                <th className="p-4 text-left font-medium">Description</th>
                <th className="p-4 text-left font-medium">Created By</th>
                <th className="p-4 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {services.length > 0 ? (
                services.map((service) => (
                  <tr key={service._id} className="border-b hover:bg-gray-100">
                    <td className="p-4 text-gray-800 font-medium">{service.serviceName}</td>
                    <td className="p-4 text-gray-600">{service.description}</td>
                    <td className="p-4 text-gray-600">{service.createdBy}</td>
                    <td className="p-4 flex gap-4">
                      <button className="text-blue-600" onClick={() => handleEdit(service)}>
                        <Edit size={18} />
                      </button>
                      {!(service?.isDeleted) &&
                        <button className="text-red-600" onClick={() => handleDelete(service._id)}>
                        <Trash2 size={18} />
                      </button>
                      }
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="p-4 text-center text-gray-500">
                    No services found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-md flex items-center justify-center z-50">
    <div className="bg-white p-8 rounded-lg shadow-lg w-[500px]">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">{editMode ? "Edit Service" : "Add New Service"}</h3>
        <button onClick={closeModal} className="text-gray-600 hover:text-gray-800">
          <X size={22} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <input
          type="text"
          name="serviceName"
          placeholder="Service Name"
          value={formData.serviceName}
          onChange={handleChange}
          className="w-full p-3 border border-gray-300 rounded-md text-lg"
          required
        />
        <textarea
          name="description"
          placeholder="Description"
          value={formData.description}
          onChange={handleChange}
          className="w-full p-3 border border-gray-300 rounded-md text-lg min-h-[120px]"
        ></textarea>
        
        <div className="flex justify-end gap-3">
          <button type="button" onClick={closeModal} className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400">
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            disabled={loading}
          >
            {loading ? <ButtonLoader/> : editMode ? "Update Service" : "Save Service"}
          </button>
        </div>
      </form>
    </div>
  </div>
)}

    </div>
  );
};

export default ServicesPage;
